import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import {
  messagesApi,
  type BugReportDto,
  type MessageCenterMessage,
} from "../../api/messagesApi";
import { getRealtimeSocket } from "../../realtime/socketService";

export const MESSAGE_CENTER_AUDIENCE_OPTIONS = [
  { value: "all", label: "כל המשתמשים" },
  { value: "organization_admins", label: "מנהלי ארגון" },
  { value: "secretaries", label: "מזכירות" },
];

export const MESSAGE_CENTER_VOTE_ROLE_OPTIONS = [
  { value: "organization_admin", label: "מנהל ארגון" },
  { value: "secretary", label: "מזכירות" },
];

export function useMessageCenter() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);
  openRef.current = open;

  const [messages, setMessages] = useState<MessageCenterMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [canSuggest, setCanSuggest] = useState(false);
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [bugReports, setBugReports] = useState<BugReportDto[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [pendingSuggestions, setPendingSuggestions] = useState<MessageCenterMessage[]>([]);
  const [pendingSuggestionsLoading, setPendingSuggestionsLoading] = useState(false);
  const [personalUpdateSnack, setPersonalUpdateSnack] = useState(false);

  const refreshMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await messagesApi.list();
      setMessages(res.items);
      setUnreadCount(res.unreadCount);
      setIsAdmin(res.capabilities.isAdmin);
      setCanSuggest(res.capabilities.canSuggest);
      setRoleKeys(res.capabilities.roleKeys);
      return res;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshReports = useCallback(async (admin: boolean) => {
    setReportsLoading(true);
    try {
      const items = admin
        ? await messagesApi.listBugReports()
        : await messagesApi.listMyBugReports();
      setBugReports(items);
    } catch {
      setBugReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const refreshPending = useCallback(async () => {
    setPendingSuggestionsLoading(true);
    try {
      const items = await messagesApi.listPendingSuggestions();
      setPendingSuggestions(items);
    } catch {
      setPendingSuggestions([]);
    } finally {
      setPendingSuggestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshMessages();
  }, [auth.activeTenant?.tenantId, refreshMessages]);

  useEffect(() => {
    if (!open) return;
    void refreshMessages().then((res) => {
      if (!res) return;
      const maxCreated = res.items.reduce<string | null>((max, m) => {
        if (!max || m.created_at > max) return m.created_at;
        return max;
      }, null);
      void messagesApi
        .markSeen({
          lastSeenMessageCreatedAt: maxCreated,
          markPersonalSeen: true,
        })
        .then(() => refreshMessages())
        .catch(() => undefined);
    });
    void refreshReports(isAdmin);
    if (isAdmin) void refreshPending();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = getRealtimeSocket();
    const onRefresh = () => {
      void refreshMessages();
      if (openRef.current) {
        void refreshReports(isAdmin);
        if (isAdmin) void refreshPending();
      }
    };
    const onPersonal = () => {
      void refreshMessages();
      if (!openRef.current) setPersonalUpdateSnack(true);
      if (openRef.current) void refreshReports(isAdmin);
    };
    socket.on("messages:updated", onRefresh);
    socket.on("newSystemMessage", onRefresh);
    socket.on("pendingSuggestionAdded", onRefresh);
    socket.on("messageCenterPersonalUpdate", onPersonal);
    return () => {
      socket.off("messages:updated", onRefresh);
      socket.off("newSystemMessage", onRefresh);
      socket.off("pendingSuggestionAdded", onRefresh);
      socket.off("messageCenterPersonalUpdate", onPersonal);
    };
  }, [isAdmin, refreshMessages, refreshPending, refreshReports]);

  const canVoteOn = useCallback(
    (message: MessageCenterMessage) => {
      if (message.type !== "suggestion") return false;
      if (message.suggestion_status && message.suggestion_status !== "approved") return false;
      const allowed = message.vote_allowed_roles ?? [];
      return allowed.some((r) => roleKeys.includes(r));
    },
    [roleKeys],
  );

  const handleMarkSeen = useCallback(() => {
    const maxCreated = messages.reduce<string | null>((max, m) => {
      if (!max || m.created_at > max) return m.created_at;
      return max;
    }, null);
    void messagesApi
      .markSeen({ lastSeenMessageCreatedAt: maxCreated, markPersonalSeen: true })
      .then(() => refreshMessages());
  }, [messages, refreshMessages]);

  const handleVote = useCallback(
    async (messageId: string, rating: number) => {
      await messagesApi.vote(messageId, rating);
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleSubmitReport = useCallback(
    async ({ title, content }: { title: string; content: string }) => {
      await messagesApi.createBugReport({ title, content });
      await refreshReports(isAdmin);
      await refreshMessages();
    },
    [isAdmin, refreshMessages, refreshReports],
  );

  const handleSubmitSuggestion = useCallback(
    async ({
      title,
      content,
      voteAllowedRoles,
    }: {
      title: string;
      content: string;
      voteAllowedRoles?: string[];
    }) => {
      await messagesApi.createSuggestion({ title, content, voteAllowedRoles });
      await refreshMessages();
      if (isAdmin) await refreshPending();
    },
    [isAdmin, refreshMessages, refreshPending],
  );

  const handleSubmitSystemMessage = useCallback(
    async ({
      type,
      title,
      content,
      audience,
    }: {
      type: "system" | "whats_new";
      title: string;
      content: string;
      audience?: string;
    }) => {
      await messagesApi.createSystemMessage({ type, title, content, audience });
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleReportUpdate = useCallback(
    async (reportId: string, patch: { status?: BugReportDto["status"]; adminNote?: string }) => {
      await messagesApi.updateBugReport(reportId, patch);
      setBugReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? {
                ...r,
                status: patch.status ?? r.status,
                admin_note:
                  patch.adminNote !== undefined ? patch.adminNote : r.admin_note,
              }
            : r,
        ),
      );
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleReportStatusChange = useCallback(
    async (reportId: string, status: BugReportDto["status"]) => {
      await handleReportUpdate(reportId, { status });
    },
    [handleReportUpdate],
  );

  const handleDeleteBugReport = useCallback(
    async (reportId: string) => {
      await messagesApi.deleteBugReport(reportId);
      setBugReports((prev) => prev.filter((r) => r.id !== reportId));
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleApproveSuggestion = useCallback(
    async (id: string, note?: string, voteRoles?: string[]) => {
      await messagesApi.approveSuggestion(id, note, voteRoles);
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleRejectSuggestion = useCallback(
    async (id: string, note?: string) => {
      await messagesApi.rejectSuggestion(id, note);
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleHandleSuggestion = useCallback(
    async (id: string, note?: string) => {
      await messagesApi.handleSuggestion(id, note);
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleDeleteSuggestion = useCallback(
    async (id: string) => {
      await messagesApi.deleteMessage(id);
      setPendingSuggestions((prev) => prev.filter((s) => s.id !== id));
      await refreshMessages();
    },
    [refreshMessages],
  );

  const handleMessageAction = useCallback(
    (message: MessageCenterMessage) => {
      if (!message?.action_url) return;
      setOpen(false);
      navigate(message.action_url);
    },
    [navigate],
  );

  return {
    open,
    setOpen,
    messages,
    loading,
    unreadCount,
    isAdmin,
    canSuggest,
    canVoteOn,
    bugReports,
    reportsLoading,
    pendingSuggestions,
    pendingSuggestionsLoading,
    personalUpdateSnack,
    setPersonalUpdateSnack,
    currentUserId: auth.user?.id ?? null,
    handleMarkSeen,
    handleVote,
    handleSubmitReport,
    handleSubmitSuggestion,
    handleSubmitSystemMessage,
    handleReportStatusChange,
    handleReportUpdate,
    handleDeleteBugReport,
    handleApproveSuggestion,
    handleRejectSuggestion,
    handleHandleSuggestion,
    handleDeleteSuggestion,
    handleMessageAction,
    audienceOptions: MESSAGE_CENTER_AUDIENCE_OPTIONS,
    voteRoleOptions: MESSAGE_CENTER_VOTE_ROLE_OPTIONS,
  };
}
